import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useSettings, useUpdateSettings } from '../../hooks/useSettings';
import { ApiError } from '../../lib/api';

export const Route = createFileRoute('/_auth/settings')({
  component: SettingsPage,
});

interface ThresholdRow {
  year: string;
  amount: string;
}

function SettingsPage() {
  const { data, isLoading, error } = useSettings();
  const update = useUpdateSettings();
  const [rows, setRows] = useState<ThresholdRow[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data && !initialized) {
      const entries = Object.entries(data.nec1099Thresholds).sort(
        (a, b) => Number(a[0]) - Number(b[0]),
      );
      setRows(entries.map(([year, amount]) => ({ year, amount: String(amount) })));
      setInitialized(true);
    }
  }, [data, initialized]);

  const setRow = (i: number, patch: Partial<ThresholdRow>) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () =>
    setRows((prev) => [...prev, { year: String(new Date().getFullYear()), amount: '' }]);
  const removeRow = (i: number) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const errorMessage =
    update.error instanceof ApiError
      ? (update.error.body as { error?: string } | null)?.error ?? 'Save failed'
      : update.error?.message;

  const save = () => {
    setSaved(false);
    const thresholds: Record<string, number> = {};
    for (const r of rows) {
      const year = r.year.trim();
      const amount = Number(r.amount);
      if (!/^\d{4}$/.test(year) || !Number.isFinite(amount) || amount < 0) continue;
      thresholds[year] = amount;
    }
    update.mutate(
      { nec1099Thresholds: thresholds },
      { onSuccess: () => setSaved(true) },
    );
  };

  return (
    <div>
      <h1 style={{ fontSize: 24, margin: '0 0 4px' }}>Settings</h1>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        App-wide settings. Company-specific options (fiscal year, period lock, consolidation) live on
        each company.
      </p>

      {/* Companies used to sit in the sidebar. It is configuration you touch a
        * few times a year, not daily work, so it lives here now. */}
      <div className="card accent-people" style={{ padding: 20, maxWidth: 520, marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, marginTop: 0 }}>Companies</h2>
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
          Add or deactivate a company, and set each one&rsquo;s fiscal year, period lock,
          consolidation and processor-fee account.
        </p>
        <Link to="/companies" className="btn-secondary" style={{ display: 'inline-block' }}>
          Manage companies
        </Link>
      </div>

      <div className="card" style={{ padding: 20, maxWidth: 520, marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, marginTop: 0 }}>Check printing</h2>
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
          Which accounts may write checks, their routing and account numbers, signature images,
          the MICR font, and printer alignment.
        </p>
        <Link to="/settings/check-printing" className="btn-secondary" style={{ display: 'inline-block' }}>
          Set up check printing
        </Link>
      </div>

      <div className="card" style={{ padding: 20, maxWidth: 520, marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, marginTop: 0 }}>Participation tracking</h2>
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
          The Geofency webhook and its token, which location belongs to which business, and the
          raw events the phone has sent.
        </p>
        <Link to="/settings/participation" className="btn-secondary" style={{ display: 'inline-block' }}>
          Set up participation tracking
        </Link>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load settings.</p>}

      {data && (
        <div className="card" style={{ padding: 20, maxWidth: 520 }}>
          <h2 style={{ fontSize: 16, marginTop: 0 }}>1099-NEC reporting thresholds</h2>
          <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
            The minimum nonemployee compensation that requires a 1099-NEC, by tax year. The report
            uses the threshold for the year being viewed.
          </p>

          <table className="table" style={{ marginBottom: 12 }}>
            <thead>
              <tr>
                <th style={{ width: 120 }}>Tax year</th>
                <th style={{ textAlign: 'right' }}>Threshold (USD)</th>
                <th style={{ width: 1 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    <input
                      value={r.year}
                      onChange={(e) => setRow(i, { year: e.target.value })}
                      maxLength={4}
                      style={{ width: 90 }}
                      placeholder="2026"
                    />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={r.amount}
                      onChange={(e) => setRow(i, { amount: e.target.value })}
                      style={{ width: 120, textAlign: 'right' }}
                      placeholder="600"
                    />
                  </td>
                  <td>
                    <button className="btn-danger" onClick={() => removeRow(i)} title="Remove">
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button className="btn-secondary" onClick={addRow}>+ Add year</button>

          {errorMessage && <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>}

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16 }}>
            <button className="btn-primary" onClick={save} disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save settings'}
            </button>
            {saved && !update.isPending && <span className="tag ok">saved</span>}
          </div>
        </div>
      )}
    </div>
  );
}
